// eslint-disable-next-line no-restricted-imports -- navigation must not import the businessObjects barrel (it pulls RecordPage back through views)
import { extractDataRecord, extractRecordPk } from "~/features/businessObjects/api/recordParsers";
import { hasApiErrorStatus } from "~/lib/apiClient";

import { type RecordHomeCandidate, recordHomeHref } from "./resolveRecordHome";

/** Why a record with a home list still opens on its own page. */
export type RecordHomeFallbackReason = "no-parent" | "many-parents" | "forbidden" | "failed";

/** A root record the record belongs to, offered when there is more than one. */
export interface RecordHomeParent {
  id: string;
  label: string;
  href: string;
}

/** The parent lookup's state, as the query reports it. */
export type RecordHomeLookup =
  | { status: "pending" }
  | { status: "error"; error: unknown }
  | { status: "success"; records: readonly unknown[] };

export type RecordHomeOutcome =
  | { status: "resolving" }
  | { status: "none" }
  | { status: "redirect"; to: string }
  | { status: "fallback"; reason: RecordHomeFallbackReason; parents: RecordHomeParent[] };

const LABEL_FIELDS = ["name", "code", "title"] as const;

const parentLabel = (data: Record<string, unknown> | null, id: string): string => {
  for (const field of LABEL_FIELDS) {
    const value = data?.[field];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return id;
};

const parentsOf = (
  home: RecordHomeCandidate,
  records: readonly unknown[],
  recordId: string,
  from: string | undefined,
): RecordHomeParent[] => {
  const parents: RecordHomeParent[] = [];
  const seen = new Set<string>();
  for (const record of records) {
    const data = extractDataRecord(record);
    const id = extractRecordPk(record) ?? extractRecordPk(data);
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    parents.push({
      id,
      label: parentLabel(data, id),
      href: recordHomeHref(home, id, recordId, from),
    });
  }

  return parents;
};

/**
 * What the record route does once the parent lookup answers: one parent redirects to it; none,
 * several, a refusal or a failure keep the record's own page and say why. A 404 means the record
 * itself is missing, which its own page already reports, so nothing is added.
 */
export const recordHomeOutcome = (
  home: RecordHomeCandidate,
  lookup: RecordHomeLookup,
  recordId: string,
  from: string | undefined,
): RecordHomeOutcome => {
  if (lookup.status === "pending") {
    return { status: "resolving" };
  }

  if (lookup.status === "error") {
    if (hasApiErrorStatus(lookup.error, 404)) {
      return { status: "none" };
    }

    return {
      status: "fallback",
      reason: hasApiErrorStatus(lookup.error, 403) ? "forbidden" : "failed",
      parents: [],
    };
  }

  const parents = parentsOf(home, lookup.records, recordId, from);
  const only = parents.length === 1 ? parents[0] : undefined;
  if (only) {
    return { status: "redirect", to: only.href };
  }

  return {
    status: "fallback",
    reason: parents.length === 0 ? "no-parent" : "many-parents",
    parents,
  };
};
